import { throttle } from 'lodash';
import React, { useCallback, useEffect } from 'react';
import { getRecord, useList, useRecord } from '../../../services/deepstream';
import { insertNode, moveNode, setDraggedNodeId } from '../../../services/board/board';
import { useCurrentBoardState } from '../../../services/state';
import { splitTextToLines, getTextRadius } from '../../../text-utils';
import { MyEmitter } from '../../../events';
import { createDragImage } from '../../../services/drag-image';

import './GraphNode.scss';

const borderWidthInPx = 3;

const GraphNode = ({ node, drawBoard }) => {
  const boardState = useCurrentBoardState();
  const { id: boardId, activeDropTargetId, allowZoomToCircle, moveWithChildren } = boardState.get();
  const { color, role, title } = useRecord(`${boardId}/circle/${node.id}`);

  const dropTargetClass = activeDropTargetId === node.id ? 'active-drop-target' : '';

  const size = 2 * node.r + borderWidthInPx + 'px';
  const titleIsNotEmpty = Boolean(title && title.trim());
  const titleRadius = getTextRadius(title);
  const titleScale = Math.min(1.5, node.r / titleRadius);
  const dropTargetId = role === 'circle' ? node.data.parentId : node.id;
  const zoomTargetId = allowZoomToCircle ? node.id : dropTargetId;

  const draggedNodeEntries = useList(`board/${boardId}/dragged-nodes`);
  const draggedNodeIds = draggedNodeEntries.map(entry => entry.split('/')[1]);
  const descendantOfDraggedNode = node
    .ancestors()
    .some(ancestor => draggedNodeIds.includes(ancestor.id));

  useEffect(() => {
    const nodeRecord = getRecord(`${boardId}/circle/${node.id}`);
    nodeRecord.subscribe('parentId', drawBoard, false);

    return () => {
      nodeRecord.unsubscribe('parentId', drawBoard);
    };
  }, [boardId, node.id, drawBoard]);

  const handleDrop = useCallback(
    event => {
      event.stopPropagation();
      const circleId = event.dataTransfer.getData('circleId');
      const internalDrag = event.dataTransfer.getData('internal');
      setDraggedNodeId(null);
      boardState.activeDropTargetId.set(null);

      if (descendantOfDraggedNode) return;

      if (internalDrag) {
        moveNode({ nodeId: circleId, targetNodeId: dropTargetId, withChildren: moveWithChildren });
      } else {
        insertNode({ circleId: circleId.split('/')[2], parentId: dropTargetId });
      }
    },
    [dropTargetId, boardState.activeDropTargetId, moveWithChildren, descendantOfDraggedNode]
  );

  const handleDragStart = useCallback(
    event => {
      event.dataTransfer.setData('circleId', node.id);
      event.dataTransfer.setData('internal', true);
      event.dataTransfer.setDragImage(createDragImage(`graph-node-${node.id}`), node.r, node.r);
      setDraggedNodeId(node.id);
    },
    [node.id, node.r]
  );

  const handleDragEnter = useCallback(
    event => {
      if (descendantOfDraggedNode) return;

      event.preventDefault();
      event.stopPropagation();
      boardState.activeDropTargetId.set(dropTargetId);
    },
    [boardState.activeDropTargetId, dropTargetId, descendantOfDraggedNode]
  );

  const handleWheelEvent = useCallback(
    throttle(event => {
      event.stopPropagation();
      const direction = event.nativeEvent.wheelDelta < 0 ? 'out' : 'in';
      if (direction === 'out') {
        boardState.zoomNodeId.set(null);
      } else {
        boardState.zoomNodeId.set(zoomTargetId);
      }
    }, 100),
    [boardState.zoomNodeId, zoomTargetId]
  );

  const openNodeEditor = useCallback(() => {
    boardState.editedNodeId.set(node.id);
    MyEmitter.dispatch('circleChange', {
      role,
      color,
      title,
      id: node.data.id,
      parentId: node.data.parentId,
      background: ''
    });
  }, [node, role, color, title, boardState.editedNodeId]);

  return (
    <div
      id={`graph-node-${node.id}`}
      className={`graph-node ${role} ${dropTargetClass}`}
      style={{
        border: `${borderWidthInPx}px solid ${color}`,
        backgroundColor: role === 'circle' ? color : 'transparent',
        position: 'absolute',
        borderRadius: '50%',
        top: node.y - node.r + 'px',
        left: node.x - node.r + 'px',
        width: size,
        height: size,
        opacity: descendantOfDraggedNode ? 0.3 : 1
      }}
      draggable={true}
      onClick={openNodeEditor}
      onDragStart={handleDragStart}
      onDragEnter={handleDragEnter}
      onDragOver={event => event.preventDefault()}
      onDrop={handleDrop}
      onWheel={handleWheelEvent}
    >
      {titleIsNotEmpty && role === 'circle' && (
        <div className="circle-title" style={{ transform: `scale(${titleScale})` }}>
          {splitTextToLines(title).map(({ text, width }, index) => (
            <div key={index} style={{ width: `${width}px` }}>
              {text}
            </div>
          ))}
        </div>
      )}
      {titleIsNotEmpty && role === 'group' && (
        <div className="group-title" style={{ backgroundColor: color }}>
          {title}
        </div>
      )}
    </div>
  );
};

export default GraphNode;
